import { useEffect, useMemo, useState } from "react";
import { Loader2, Brain, Layers, BookOpen, Languages, AlertCircle, Mic, Database } from "lucide-react";
import AdminShell from "../components/AdminShell";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";
import { Toast } from "../components/AdminUI";

const TOKEN_KEY = "nailEvent.adminToken";

export default function AdminTrainingDashboard() {
  const { lang } = useI18n();
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const L = (vi, en) => (lang === "en" ? en : vi);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/ai/training-dashboard", { headers });
      setStats(r.data);
    } catch (err) { setToast(err?.response?.data?.detail || "Error"); setTimeout(() => setToast(""), 2500); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const cards = stats ? [
    { icon: BookOpen, label: L("Tổng tài liệu", "Total documents"), value: stats.items_total, accent: "emerald" },
    { icon: BookOpen, label: L("Đã xuất bản", "Published"), value: stats.items_published, accent: "emerald" },
    { icon: Brain, label: L("AI dùng được", "Visible to AI"), value: stats.items_visible_ai, accent: "gold" },
    { icon: AlertCircle, label: L("Đợi ingest", "Pending ingestion"), value: stats.pending_ingestion, accent: "warn" },
    { icon: Layers, label: L("Bộ sưu tập", "Collections"), value: stats.collections_total, accent: "emerald" },
    { icon: Brain, label: L("Agent đang bật", "Active agents"), value: `${stats.agents_active}/${stats.agents_total}`, accent: "gold" },
    { icon: Database, label: L("Vector embeddings (Phase 5)", "Vector embeddings (Phase 5)"), value: stats.vector_count, accent: "muted" },
    { icon: Mic, label: L("Voice coverage (Phase 5)", "Voice coverage (Phase 5)"), value: stats.voice_coverage, accent: "muted" },
  ] : [];

  return (
    <AdminShell
      testid="admin-training-dashboard"
      subtitle={L("Một Bộ Não AI", "One AI Brain")}
      title={L("Bảng Huấn Luyện AI", "AI Training Dashboard")}
    >
      <div className="max-w-[1400px]">
        <Toast message={toast} testId="training-toast" />

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-espresso-muted" /></div>
        ) : !stats ? null : (
          <>
            <div className="bg-white border border-espresso/10 rounded-3xl p-6 md:p-8 mb-6">
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-emerald2">{L("Nguyên tắc nền tảng", "Platform principle")}</p>
              <h2 className="font-serif text-2xl md:text-3xl text-espresso mt-2">{L("Một Kho Tri Thức. Một Bộ Não AI. Nhiều Giao Diện.", "One Knowledge Base. One AI Brain. Many Interfaces.")}</h2>
              <p className="mt-2 text-sm text-espresso-soft max-w-3xl">
                {L(
                  "Mỗi tài liệu, bộ sưu tập, agent ở đây phục vụ AI Chat, AI Voice tương lai, Mobile, và mọi kênh sắp tới. Một lần tải lên — toàn bộ nền tảng hưởng lợi.",
                  "Every document, collection, and agent here serves AI Chat, future AI Voice, Mobile, and every upcoming channel. Upload once — the whole platform benefits."
                )}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {cards.map((c, i) => {
                const Icon = c.icon;
                const accentClass = c.accent === "emerald" ? "text-emerald2 bg-emerald2/10"
                  : c.accent === "gold" ? "text-gold-500 bg-gold-500/10"
                  : c.accent === "warn" ? "text-amber-700 bg-amber-100"
                  : "text-espresso-muted bg-linen-100";
                return (
                  <div key={i} data-testid={`stat-card-${i}`} className="bg-white border border-espresso/10 rounded-3xl p-5">
                    <div className={`inline-flex w-9 h-9 rounded-2xl items-center justify-center ${accentClass}`}><Icon className="w-4 h-4" /></div>
                    <div className="mt-3 font-serif text-3xl text-espresso leading-none">{c.value}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-espresso-muted">{c.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white border border-espresso/10 rounded-3xl p-5 md:p-6">
                <div className="flex items-center gap-2 mb-3"><Languages className="w-4 h-4 text-emerald2" /><h3 className="font-serif text-xl text-espresso">{L("Bao phủ ngôn ngữ", "Language coverage")}</h3></div>
                {stats.language_coverage.length === 0 ? (
                  <p className="text-sm text-espresso-soft">{L("Chưa có dữ liệu.", "No data yet.")}</p>
                ) : (
                  <div className="space-y-2">
                    {stats.language_coverage.map((r) => (
                      <div key={r.language} className="flex items-center justify-between text-sm">
                        <span className="font-medium uppercase text-espresso">{r.language}</span>
                        <span className="text-espresso-muted">{r.count} {L("tài liệu", "docs")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-white border border-espresso/10 rounded-3xl p-5 md:p-6">
                <div className="flex items-center gap-2 mb-3"><Brain className="w-4 h-4 text-emerald2" /><h3 className="font-serif text-xl text-espresso">{L("Bao phủ chuyên môn", "Category coverage")}</h3></div>
                {stats.category_coverage.length === 0 ? (
                  <p className="text-sm text-espresso-soft">{L("Tải tài liệu IRS, OSHA, State Board, AsterPOS... để bắt đầu.", "Upload IRS, OSHA, State Board, AsterPOS docs to begin.")}</p>
                ) : (
                  <div className="space-y-2">
                    {stats.category_coverage.slice(0, 10).map((r) => (
                      <div key={r.category} className="flex items-center justify-between text-sm">
                        <span className="text-espresso">{r.category}</span>
                        <span className="text-espresso-muted">{r.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}
